import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Crown, ArrowLeft, LogOut, Home } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAmbientAudio } from "@/context/AudioContext";
import SigilField from "@/components/SigilField";
import Ecosystem from "@/components/Ecosystem";
import CouncilCircles from "@/components/CouncilCircles";

const ease = [0.22, 1, 0.36, 1];

export const Matriarch = () => {
  const { member, logout } = useAuth();
  const { start, setMode } = useAmbientAudio();
  const navigate = useNavigate();

  const isMatriarch = member && member.tier === "The Matriarch";

  useEffect(() => {
    if (isMatriarch) {
      (async () => {
        try {
          await start();
        } catch (e) {}
        setMode("sanctum");
      })();
    }
    return () => setMode("veil");
  }, [isMatriarch, start, setMode]);

  if (member === null) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#040404]"
        data-testid="matriarch-loading"
      >
        <p className="animate-pulse font-serif text-2xl font-light italic text-[#C5A059]">
          The private chambers open…
        </p>
      </div>
    );
  }

  if (member === false) return <Navigate to="/gate" replace />;

  if (!member.oath_sealed) return <Navigate to="/sanctum" replace />;

  if (!isMatriarch) {
    toast.error("The Matriarch Ecosystem is sealed to your standing.");
    return <Navigate to="/sanctum" replace />;
  }

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.lenis) window.lenis.scrollTo(el, { offset: -20 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative min-h-screen w-full bg-[#040404]" data-testid="matriarch-page">
      <SigilField />

      {/* Top bar */}
      <div className="fixed left-0 right-0 top-0 z-40 flex items-center justify-between px-6 py-5 md:px-10">
        <button
          onClick={() => navigate("/sanctum")}
          className="inline-flex items-center gap-2 rounded-full border border-[#C5A059]/25 bg-[#040404]/70 px-5 py-2.5 text-[0.6rem] uppercase tracking-[0.2em] text-white/70 backdrop-blur-xl transition-colors hover:text-[#C5A059]"
          data-testid="matriarch-back-sanctum-btn"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Inner Sanctum
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 rounded-full border border-[#C5A059]/20 bg-[#040404]/70 px-4 py-2.5 text-white/50 backdrop-blur-xl transition-colors hover:text-[#C5A059]"
            data-testid="matriarch-home-btn"
            title="Return to the Veil"
          >
            <Home size={16} strokeWidth={1.5} />
          </button>
          <div className="flex items-center gap-4 rounded-full border border-[#C5A059]/20 bg-[#040404]/70 px-5 py-2.5 backdrop-blur-xl">
            <div className="text-right">
              <p className="text-xs font-light text-white/80" data-testid="matriarch-member-name">
                {member.name}
              </p>
              <p className="text-[0.6rem] uppercase tracking-[0.2em] text-[#C5A059]">
                {member.tier}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="text-white/40 transition-colors hover:text-[#C5A059]"
              data-testid="matriarch-logout-btn"
              title="Seal the veil behind you"
            >
              <LogOut size={17} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-6 pb-28 pt-32 md:px-12">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease }}
          data-testid="matriarch-hero"
        >
          <div className="flex items-center gap-3 text-[#C5A059]">
            <Crown size={18} strokeWidth={1.5} />
            <p className="overline">The Matriarch — Private Council</p>
          </div>
          <h1 className="mt-6 font-serif text-[clamp(2.6rem,8vw,6rem)] font-light leading-[0.95] tracking-tight text-white">
            The Sovereign <br />
            <span className="italic text-[#C5A059]">Chambers</span>.
          </h1>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/55">
            These halls answer to The Matriarch alone. Here capital is pooled, time is ruled by the
            heavens, and the circles convene in sealed council. What passes here does not leave.
          </p>
        </motion.div>

        {/* The Ecosystem */}
        <Ecosystem member={member} onNavigate={scrollTo} />

        {/* Council Circles */}
        <div id="council-circles">
          <CouncilCircles />
        </div>
      </div>
    </div>
  );
};

export default Matriarch;
